function extractEightDigit(...candidates: (string | null | undefined)[]): string | null {
  for (const raw of candidates) {
    if (raw == null) continue
    const s = String(raw).trim()
    if (/^\d{8}$/.test(s)) return s
    const m = s.match(/\b(\d{8})\b/)
    if (m) return m[1]
  }
  return null
}

function stripJobNumberPrefix(value: string, jobNumber: string | null): string {
  if (!jobNumber) return value.trim()
  const stripped = value.replace(/^\d{8}\s*[-–—:|]\s*/u, '').trim()
  if (stripped && stripped !== jobNumber && !/^\d{8}$/.test(stripped)) return stripped
  return value.trim()
}

function isPlaceholder(value: string | undefined | null): boolean {
  const s = value?.trim()
  return !s || s === '—' || s === 'Untitled'
}

/** Brevity job numbers are 8-digit PWF project numbers (e.g. 23020037). */
export function normalizeEightDigitProjectNumber(value: string | null | undefined): string {
  return extractEightDigit(value) ?? '—'
}

/**
 * Resolve display fields from raw PWF / snapshot values.
 * Handles legacy snapshots where number and title were stored in one field.
 */
export function resolvePwfProjectDisplayFields(input: {
  projectNumber?: string | null
  projectTitle?: string | null
  projectName?: string | null
  fallbackTitle?: string | null
}): { projectNumber: string; projectTitle: string } {
  const rawNumber = (input.projectNumber ?? '').trim()
  const rawTitle = (input.projectTitle ?? input.projectName ?? '').trim()
  const fallback = (input.fallbackTitle ?? '').trim()

  const jobNumber = extractEightDigit(rawNumber, rawTitle)
  const projectNumber = jobNumber ?? '—'

  const titleCandidates = [rawTitle, rawNumber, fallback]
  for (const raw of titleCandidates) {
    if (!raw || isPlaceholder(raw)) continue
    if (jobNumber && raw === jobNumber) continue
    if (/^\d{8}$/.test(raw)) continue
    const stripped = stripJobNumberPrefix(raw, jobNumber)
    if (stripped && stripped !== jobNumber && !/^\d{8}$/.test(stripped)) {
      return { projectNumber, projectTitle: stripped }
    }
    if (!/^\d{8}$/.test(raw)) {
      return { projectNumber, projectTitle: raw }
    }
  }

  return { projectNumber, projectTitle: fallback && !isPlaceholder(fallback) ? fallback : 'Untitled' }
}

export function pickBetterProjectTitle(
  ...candidates: (string | null | undefined)[]
): string {
  for (const raw of candidates) {
    const s = raw?.trim()
    if (s && !isPlaceholder(s)) return s
  }
  return 'Untitled'
}
